import type {
  ListTableColumn,
  ListTableColumnKey,
  ListTableSelectedRowsState,
  UseListTableColumnsProps,
  UseListTableSelectedRowsProps,
} from "./list-table.type";
import type { ListItem } from "@/modules/list";
import type { ModelKey } from "@/modules/model";
import type { TableProps } from "antd";
import type { Key } from "react";

import omitBy from "lodash/omitBy";
import startsWith from "lodash/startsWith";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGetCurrentUser } from "@/entities/current-user";
import { useAsyncActions } from "@/modules/async-actions";
import { useListContext, useListParams } from "@/modules/list";
import { isValidObject } from "@/utilities/object";

import {
  getListTableFilterColumnKey,
  getListTableSortColumnKey,
  userCanFilterListTableColumn,
} from "./list-table.helper";

export const useListTableRowClassName = (modelKey?: ModelKey) => {
  const { isInDeletingState, isInUpdatedState, isInFailedState } =
    useAsyncActions();

  const getRowClassName = useCallback(
    (record: ListItem) => {
      if (!modelKey || !record.id) return "";
      if (isInDeletingState(modelKey, record.id))
        return "list-table-row-deleting";
      if (isInFailedState(modelKey, record.id)) return "list-table-row-failed";
      if (isInUpdatedState(modelKey, record.id))
        return "list-table-row-updated";
      return "";
    },
    [modelKey, isInDeletingState, isInUpdatedState, isInFailedState]
  );

  return { getRowClassName };
};

export const useListTableSelectedRows = <Item extends ListItem = ListItem>({
  onSelectedItemsChange,
  isBatchMode,
}: UseListTableSelectedRowsProps<Item>) => {
  const { fetching } = useListContext();

  const [selectedRows, setSelectedRows] = useState<
    ListTableSelectedRowsState<Item>
  >({ keys: [], items: [] });

  const onSelectedRowsChange = (keys: Key[], items: Item[]) => {
    setSelectedRows({ keys, items });
  };

  const resetSelectedRows = () => {
    setSelectedRows({ keys: [], items: [] });
  };

  useEffect(() => {
    onSelectedItemsChange?.(selectedRows.items);
  }, [selectedRows]);

  useEffect(() => {
    if (fetching) resetSelectedRows();
  }, [fetching]);

  const rowSelection: TableProps<Item>["rowSelection"] = {
    selectedRowKeys: selectedRows.keys,
    onChange: onSelectedRowsChange,
  };

  return {
    selectedItems: selectedRows.items,
    rowSelection: isBatchMode ? rowSelection : undefined,
  };
};

export const useListTableColumns = <Item extends ListItem = ListItem>({
  columns = [],
}: UseListTableColumnsProps<Item>) => {
  const { multipleSort, setTableColumnsKeys } = useListContext();
  const { filters, sort } = useListParams();
  const { currentUser } = useGetCurrentUser();

  const formattedColumns = useMemo(() => {
    if (!columns?.length) return [];

    const filteredColumns = columns.map((column) =>
      !userCanFilterListTableColumn({ user: currentUser, column })
        ? (omitBy(column, (_, key) =>
            startsWith(key, "filter", 0)
          ) as ListTableColumn<Item>)
        : column
    );

    return filteredColumns.map((column): ListTableColumn<Item> => {
      const columnKeyForFilter = getListTableFilterColumnKey(column);
      const columnKeyForSort = getListTableSortColumnKey(column);

      /**
       * Handle filter props
       */

      // AntD log errors when filteredValue is used only for some columns
      // because it means the filters state of the table is now controled by
      // the props but not every columns is handling it. In this case, we
      // need to keep track of the table filters returned by AntD. And use
      // it to define filteredValue for each column. Always fallback to null
      // if the column do not have a corresponding filteredValue.
      const filteredValue =
        column.filteredValue ?? filters[columnKeyForFilter] ?? null;

      /**
       * Handle sort props
       */

      // Force multiple sortable for all sortable columns
      let sorter = column.sorter;
      if (multipleSort && sorter) {
        const existingValueFormatted = isValidObject(sorter) ? sorter : {}; // handle `sorter: true` & `sorter: { compare: fn() }`
        sorter = { ...existingValueFormatted, multiple: 1 };
      }

      // Make sure the sorted column is highligted on ListTable
      const sortOrder = sort.includes(columnKeyForSort)
        ? "ascend"
        : sort.includes(`-${columnKeyForSort}`)
          ? "descend"
          : undefined;

      /**
       * Re-format column props based on hardcoded filter & sort props
       */

      return {
        ...column,
        filteredValue,
        sorter,
        sortOrder,
        showSorterTooltip: false, // hide sort tooltip by default to show special tooltip (ex: column with icons)
      };
    });
  }, [currentUser, columns, filters, sort]);

  useEffect(() => {
    const columnKeys = formattedColumns
      .filter((column) => !column.hidden)
      .map(
        (column): ListTableColumnKey => ({
          dataIndex: column.dataIndex,
          filterKey: getListTableFilterColumnKey(column),
          sortKey: getListTableSortColumnKey(column),
        })
      );

    setTableColumnsKeys(columnKeys);
  }, [formattedColumns, setTableColumnsKeys]);

  return formattedColumns;
};
